package models

import kotlinx.serialization.Serializable

@Serializable
data class PlaidCategory(
    val primary: String,
    val detailed: String,
    val description: String? = null
)

object PlaidCategories {
    // Primary Categories
    const val INCOME = "INCOME"
    const val TRANSFER_IN = "TRANSFER_IN"
    const val TRANSFER_OUT = "TRANSFER_OUT"
    const val LOAN_PAYMENTS = "LOAN_PAYMENTS"
    const val BANK_FEES = "BANK_FEES"
    const val ENTERTAINMENT = "ENTERTAINMENT"
    const val FOOD_AND_DRINK = "FOOD_AND_DRINK"
    const val GENERAL_MERCHANDISE = "GENERAL_MERCHANDISE"
    const val HOME_IMPROVEMENT = "HOME_IMPROVEMENT"
    const val MEDICAL = "MEDICAL"
    const val PERSONAL_CARE = "PERSONAL_CARE"
    const val GENERAL_SERVICES = "GENERAL_SERVICES"
    const val GOVERNMENT_AND_NON_PROFIT = "GOVERNMENT_AND_NON_PROFIT"
    const val TRANSPORTATION = "TRANSPORTATION"
    const val TRAVEL = "TRAVEL"
    const val RENT_AND_UTILITIES = "RENT_AND_UTILITIES"
    const val FAMILY = "FAMILY"

    /**
     * True when a transaction is an internal money-movement between the user's
     * OWN accounts (TRANSFER_IN / TRANSFER_OUT) — NOT real income or spending,
     * and it may still feed the Savings bar when assigned to a goal.
     *
     * Every cash-flow surface — the Monthly Overview income/expense bars,
     * spending totals, and the savings-rate denominator — MUST exclude these so
     * a transfer to savings isn't double-counted as an expense. This is the
     * SINGLE SOURCE OF TRUTH for that rule across web, Android, iOS, and server;
     * never re-derive it inline.
     *
     * DELIBERATELY NOT here: credit-card payments. A card payment IS excludable
     * (its charges are already counted on the card), but only when the card is
     * TRACKED — an unpaired payment to an untracked card is real spending that
     * must count. That "exclude only if paired" decision can't be made from the
     * category alone (it needs the matching card-side leg), so it's handled at
     * ingestion via [isCreditCardPayment] + pairing → is_excluded, NOT here.
     * (Mortgage / car / student-loan payments are genuine expenses either way.)
     *
     * Robust to both Plaid's single-underscore detailed form
     * ("TRANSFER_OUT_SAVINGS") and the app's historical double-underscore form
     * ("TRANSFER_OUT__SAVINGS").
     */
    fun isTransfer(primary: String?, detailed: String?): Boolean {
        val p = primary?.uppercase() ?: return false
        return p == TRANSFER_IN || p == TRANSFER_OUT
    }

    /**
     * True for a credit-card payment (a `LOAN_PAYMENTS` transaction whose
     * detailed code is a credit-card payment). Unlike [isTransfer] this does
     * NOT by itself exclude the transaction from cash flow — the ingestion
     * pairing pass decides that (exclude only when it pairs with a tracked
     * card's payment-credit; an unpaired payment to an untracked card counts
     * as spending). Used by that pass, the demo seeder, and the "auto-excluded"
     * badge. Matches both single- and double-underscore detailed forms.
     */
    fun isCreditCardPayment(primary: String?, detailed: String?): Boolean =
        primary?.uppercase() == LOAN_PAYMENTS &&
            detailed?.uppercase()?.contains("CREDIT_CARD_PAYMENT") == true

    // Full Taxonomy List (Subset of common categories)
    val allCategories = listOf(
        // INCOME
        PlaidCategory(INCOME, "INCOME_DIVIDENDS", "Dividends"),
        PlaidCategory(INCOME, "INCOME_INTEREST_EARNED", "Interest Earned"),
        PlaidCategory(INCOME, "INCOME_RETIREMENT_PENSION", "Retirement Pension"),
        PlaidCategory(INCOME, "INCOME_TAX_REFUND", "Tax Refund"),
        PlaidCategory(INCOME, "INCOME_UNEMPLOYMENT", "Unemployment"),
        PlaidCategory(INCOME, "INCOME_WAGES", "Wages"),
        PlaidCategory(INCOME, "INCOME_OTHER_INCOME", "Other Income"),

        // TRANSFER_IN
        PlaidCategory(TRANSFER_IN, "TRANSFER_IN_CASH_ADVANCES_AND_LOANS", "Cash Advances and Loans"),
        PlaidCategory(TRANSFER_IN, "TRANSFER_IN_DEPOSIT", "Deposit"),
        PlaidCategory(TRANSFER_IN, "TRANSFER_IN_INVESTMENT_AND_RETIREMENT_FUNDS", "Investment and Retirement Funds"),
        PlaidCategory(TRANSFER_IN, "TRANSFER_IN_SAVINGS", "Savings"),
        PlaidCategory(TRANSFER_IN, "TRANSFER_IN_ACCOUNT_TRANSFER", "Account Transfer"),
        PlaidCategory(TRANSFER_IN, "TRANSFER_IN_OTHER_TRANSFER_IN", "Other Transfer In"),

        // TRANSFER_OUT
        PlaidCategory(TRANSFER_OUT, "TRANSFER_OUT_INVESTMENT_AND_RETIREMENT_FUNDS", "Investment and Retirement Funds"),
        PlaidCategory(TRANSFER_OUT, "TRANSFER_OUT_SAVINGS", "Savings"),
        PlaidCategory(TRANSFER_OUT, "TRANSFER_OUT_WITHDRAWAL", "Withdrawal"),
        PlaidCategory(TRANSFER_OUT, "TRANSFER_OUT_ACCOUNT_TRANSFER", "Account Transfer"),
        PlaidCategory(TRANSFER_OUT, "TRANSFER_OUT_OTHER_TRANSFER_OUT", "Other Transfer Out"),

        // LOAN_PAYMENTS
        PlaidCategory(LOAN_PAYMENTS, "LOAN_PAYMENTS_CAR_PAYMENT", "Car Payment"),
        PlaidCategory(LOAN_PAYMENTS, "LOAN_PAYMENTS_CREDIT_CARD_PAYMENT", "Credit Card Payment"),
        PlaidCategory(LOAN_PAYMENTS, "LOAN_PAYMENTS_PERSONAL_LOAN_PAYMENT", "Personal Loan Payment"),
        PlaidCategory(LOAN_PAYMENTS, "LOAN_PAYMENTS_MORTGAGE_PAYMENT", "Mortgage Payment"),
        PlaidCategory(LOAN_PAYMENTS, "LOAN_PAYMENTS_STUDENT_LOAN_PAYMENT", "Student Loan Payment"),
        PlaidCategory(LOAN_PAYMENTS, "LOAN_PAYMENTS_OTHER_PAYMENT", "Other Payment"),

        // BANK_FEES
        PlaidCategory(BANK_FEES, "BANK_FEES_ATM_FEES", "ATM Fees"),
        PlaidCategory(BANK_FEES, "BANK_FEES_FOREIGN_TRANSACTION_FEES", "Foreign Transaction Fees"),
        PlaidCategory(BANK_FEES, "BANK_FEES_INSUFFICIENT_FUNDS", "Insufficient Funds"),
        PlaidCategory(BANK_FEES, "BANK_FEES_INTEREST_CHARGE", "Interest Charge"),
        PlaidCategory(BANK_FEES, "BANK_FEES_OVERDRAFT_FEES", "Overdraft Fees"),
        PlaidCategory(BANK_FEES, "BANK_FEES_OTHER_BANK_FEES", "Other Bank Fees"),

        // ENTERTAINMENT
        PlaidCategory(ENTERTAINMENT, "ENTERTAINMENT_CASINOS_AND_GAMBLING", "Casinos and Gambling"),
        PlaidCategory(ENTERTAINMENT, "ENTERTAINMENT_MUSIC_AND_AUDIO", "Music and Audio"),
        PlaidCategory(ENTERTAINMENT, "ENTERTAINMENT_SPORTING_EVENTS_AMUSEMENT_PARKS_AND_MUSEUMS", "Sporting Events, Amusement Parks, and Museums"),
        PlaidCategory(ENTERTAINMENT, "ENTERTAINMENT_TV_AND_MOVIES", "TV and Movies"),
        PlaidCategory(ENTERTAINMENT, "ENTERTAINMENT_VIDEO_GAMES", "Video Games"),
        PlaidCategory(ENTERTAINMENT, "ENTERTAINMENT_OTHER_ENTERTAINMENT", "Other Entertainment"),

        // FOOD_AND_DRINK
        PlaidCategory(FOOD_AND_DRINK, "FOOD_AND_DRINK_BEER_WINE_AND_LIQUOR", "Beer, Wine, and Liquor"),
        PlaidCategory(FOOD_AND_DRINK, "FOOD_AND_DRINK_COFFEE", "Coffee"),
        PlaidCategory(FOOD_AND_DRINK, "FOOD_AND_DRINK_FAST_FOOD", "Fast Food"),
        PlaidCategory(FOOD_AND_DRINK, "FOOD_AND_DRINK_GROCERIES", "Groceries"),
        PlaidCategory(FOOD_AND_DRINK, "FOOD_AND_DRINK_RESTAURANT", "Restaurant"),
        PlaidCategory(FOOD_AND_DRINK, "FOOD_AND_DRINK_VENDING_MACHINES", "Vending Machines"),
        PlaidCategory(FOOD_AND_DRINK, "FOOD_AND_DRINK_OTHER_FOOD_AND_DRINK", "Other Food and Drink"),

        // GENERAL_MERCHANDISE
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_BOOKSTORES_AND_NEWSSTANDS", "Bookstores and Newsstands"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_CLOTHING_AND_ACCESSORIES", "Clothing and Accessories"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_CONVENIENCE_STORES", "Convenience Stores"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_DEPARTMENT_STORES", "Department Stores"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_DISCOUNT_STORES", "Discount Stores"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_ELECTRONICS", "Electronics"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_GIFTS_AND_NOVELTIES", "Gifts and Novelties"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_OFFICE_SUPPLIES", "Office Supplies"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_ONLINE_MARKETPLACES", "Online Marketplaces"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_PET_SUPPLIES", "Pet Supplies"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_SPORTING_GOODS", "Sporting Goods"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_SUPERSTORES", "Superstores"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_TOBACCO_AND_VAPE", "Tobacco and Vape"),
        PlaidCategory(GENERAL_MERCHANDISE, "GENERAL_MERCHANDISE_OTHER_GENERAL_MERCHANDISE", "Other General Merchandise"),

        // HOME_IMPROVEMENT
        PlaidCategory(HOME_IMPROVEMENT, "HOME_IMPROVEMENT_FURNITURE", "Furniture"),
        PlaidCategory(HOME_IMPROVEMENT, "HOME_IMPROVEMENT_HARDWARE", "Hardware"),
        PlaidCategory(HOME_IMPROVEMENT, "HOME_IMPROVEMENT_REPAIR_AND_MAINTENANCE", "Repair and Maintenance"),
        PlaidCategory(HOME_IMPROVEMENT, "HOME_IMPROVEMENT_SECURITY", "Security"),
        PlaidCategory(HOME_IMPROVEMENT, "HOME_IMPROVEMENT_OTHER_HOME_IMPROVEMENT", "Other Home Improvement"),

        // MEDICAL
        PlaidCategory(MEDICAL, "MEDICAL_DENTAL_CARE", "Dental Care"),
        PlaidCategory(MEDICAL, "MEDICAL_EYE_CARE", "Eye Care"),
        PlaidCategory(MEDICAL, "MEDICAL_NURSING_CARE", "Nursing Care"),
        PlaidCategory(MEDICAL, "MEDICAL_PHARMACIES_AND_SUPPLEMENTS", "Pharmacies and Supplements"),
        PlaidCategory(MEDICAL, "MEDICAL_PRIMARY_CARE", "Primary Care"),
        PlaidCategory(MEDICAL, "MEDICAL_VETERINARY_SERVICES", "Veterinary Services"),
        PlaidCategory(MEDICAL, "MEDICAL_OTHER_MEDICAL", "Other Medical"),

        // PERSONAL_CARE
        PlaidCategory(PERSONAL_CARE, "PERSONAL_CARE_GYMS_AND_FITNESS_CENTERS", "Gyms and Fitness Centers"),
        PlaidCategory(PERSONAL_CARE, "PERSONAL_CARE_HAIR_AND_BEAUTY", "Hair and Beauty"),
        PlaidCategory(PERSONAL_CARE, "PERSONAL_CARE_LAUNDRY_AND_DRY_CLEANING", "Laundry and Dry Cleaning"),
        PlaidCategory(PERSONAL_CARE, "PERSONAL_CARE_OTHER_PERSONAL_CARE", "Other Personal Care"),

        // GENERAL_SERVICES
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_ACCOUNTING_AND_FINANCIAL_PLANNING", "Accounting and Financial Planning"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_AUTOMOTIVE", "Automotive"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_CHILDCARE", "Childcare"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_CONSULTING_AND_LEGAL", "Consulting and Legal"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_EDUCATION", "Education"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_INSURANCE", "Insurance"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_POSTAGE_AND_SHIPPING", "Postage and Shipping"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_STORAGE", "Storage"),
        PlaidCategory(GENERAL_SERVICES, "GENERAL_SERVICES_OTHER_GENERAL_SERVICES", "Other General Services"),

        // GOVERNMENT_AND_NON_PROFIT
        PlaidCategory(GOVERNMENT_AND_NON_PROFIT, "GOVERNMENT_AND_NON_PROFIT_DONATIONS", "Donations"),
        PlaidCategory(GOVERNMENT_AND_NON_PROFIT, "GOVERNMENT_AND_NON_PROFIT_GOVERNMENT_DEPARTMENTS_AND_AGENCIES", "Government Departments and Agencies"),
        PlaidCategory(GOVERNMENT_AND_NON_PROFIT, "GOVERNMENT_AND_NON_PROFIT_TAX_PAYMENT", "Tax Payment"),
        PlaidCategory(GOVERNMENT_AND_NON_PROFIT, "GOVERNMENT_AND_NON_PROFIT_OTHER_GOVERNMENT_AND_NON_PROFIT", "Other Government and Non-Profit"),

        // TRANSPORTATION
        PlaidCategory(TRANSPORTATION, "TRANSPORTATION_BIKES_AND_SCOOTERS", "Bikes and Scooters"),
        PlaidCategory(TRANSPORTATION, "TRANSPORTATION_GAS_STATIONS", "Gas Stations"),
        PlaidCategory(TRANSPORTATION, "TRANSPORTATION_PARKING", "Parking"),
        PlaidCategory(TRANSPORTATION, "TRANSPORTATION_PUBLIC_TRANSIT", "Public Transit"),
        PlaidCategory(TRANSPORTATION, "TRANSPORTATION_TAXIS_AND_RIDE_SHARES", "Taxis and Ride Shares"),
        PlaidCategory(TRANSPORTATION, "TRANSPORTATION_TOLLS", "Tolls"),
        PlaidCategory(TRANSPORTATION, "TRANSPORTATION_OTHER_TRANSPORTATION", "Other Transportation"),

        // TRAVEL
        PlaidCategory(TRAVEL, "TRAVEL_AIRLINES_AND_AVIATION_SERVICES", "Airlines and Aviation Services"),
        PlaidCategory(TRAVEL, "TRAVEL_LODGING", "Lodging"),
        PlaidCategory(TRAVEL, "TRAVEL_RENTAL_CARS", "Rental Cars"),
        PlaidCategory(TRAVEL, "TRAVEL_TRAVEL_AGENCIES_AND_TOUR_OPERATORS", "Travel Agencies and Tour Operators"),
        PlaidCategory(TRAVEL, "TRAVEL_OTHER_TRAVEL", "Other Travel"),

        // RENT_AND_UTILITIES
        PlaidCategory(RENT_AND_UTILITIES, "RENT_AND_UTILITIES_GAS_AND_ELECTRICITY", "Gas and Electricity"),
        PlaidCategory(RENT_AND_UTILITIES, "RENT_AND_UTILITIES_INTERNET_AND_CABLE", "Internet and Cable"),
        PlaidCategory(RENT_AND_UTILITIES, "RENT_AND_UTILITIES_RENT", "Rent"),
        PlaidCategory(RENT_AND_UTILITIES, "RENT_AND_UTILITIES_SEWAGE_AND_WASTE_MANAGEMENT", "Sewage and Waste Management"),
        PlaidCategory(RENT_AND_UTILITIES, "RENT_AND_UTILITIES_TELEPHONE", "Telephone"),
        PlaidCategory(RENT_AND_UTILITIES, "RENT_AND_UTILITIES_WATER", "Water"),
        PlaidCategory(RENT_AND_UTILITIES, "RENT_AND_UTILITIES_OTHER_UTILITIES", "Other Utilities"),

        // FAMILY
        PlaidCategory(FAMILY, "FAMILY_CHILD_SUPPORT", "Child Support"),
        PlaidCategory(FAMILY, "FAMILY_SPOUSAL_SUPPORT", "Spousal Support"),
        PlaidCategory(FAMILY, "FAMILY_ELDER_CARE", "Elder Care"),
        PlaidCategory(FAMILY, "FAMILY_OTHER_FAMILY_SUPPORT", "Other Family Support")
    )
}
